import React from "react";
import { motion } from "framer-motion";

/* =====================================================================
   THE LIFTILE PROMISE — emotional, typography-only ending.
   No icons. No cards. No decorative graphics.
   Only premium type + generous whitespace + a single orange accent line.
   ===================================================================== */

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.75, ease: "easeOut" } },
};

export default function LiftilePromiseSection() {
  return (
    <section
      data-testid="about-promise"
      className="relative bg-liftile-black py-28 md:py-40 lg:py-48 overflow-hidden"
    >
      {/* Faint blueprint grid */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.045] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
          backgroundSize: "80px 80px",
        }}
      />
      {/* Centered orange ambient halo */}
      <div
        aria-hidden
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[820px] h-[820px] rounded-full pointer-events-none opacity-25"
        style={{
          background:
            "radial-gradient(circle, rgba(255,107,26,0.32) 0%, rgba(255,107,26,0) 65%)",
          filter: "blur(60px)",
        }}
      />

      <div className="container-x relative">
        <div className="max-w-3xl mx-auto text-center">
          {/* Eyebrow */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeUp}
            className="inline-flex items-center gap-3 mb-8"
          >
            <span className="block w-10 h-px bg-liftile-orange" />
            <span className="font-heading text-[10px] md:text-xs uppercase tracking-[0.32em] text-liftile-orange">
              Our Promise
            </span>
            <span className="block w-10 h-px bg-liftile-orange" />
          </motion.div>

          {/* Heading */}
          <motion.h2
            data-testid="promise-heading"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeUp}
            transition={{ delay: 0.08 }}
            className="font-display uppercase text-liftile-cream leading-[0.95] tracking-tight text-4xl sm:text-5xl md:text-6xl"
          >
            The <span className="text-liftile-orange">LIFTILE</span> Promise
          </motion.h2>

          {/* Statement — generous line height, intentional rhythm */}
          <motion.div
            data-testid="promise-statement"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeUp}
            transition={{ delay: 0.2 }}
            className="mt-12 md:mt-16 space-y-7 font-body text-liftile-cream/82 text-lg md:text-xl lg:text-[1.35rem] leading-[1.55]"
          >
            <p>
              Every machine we manufacture represents{" "}
              <span className="text-liftile-cream">
                someone&apos;s hard-earned investment
              </span>
              , someone&apos;s project and someone&apos;s trust.
            </p>
            <p>
              That responsibility guides every decision we make — from
              engineering and material selection to manufacturing, inspection,
              testing and after-sales support.
            </p>
            <p className="text-liftile-cream/70">
              We do not simply manufacture construction machinery.
            </p>
            <p className="text-liftile-cream font-heading uppercase tracking-tight text-xl md:text-2xl lg:text-3xl pt-1">
              We build <span className="text-liftile-orange">confidence</span>{" "}
              that lasts.
            </p>
          </motion.div>

          {/* Animated orange accent line */}
          <motion.div
            initial={{ opacity: 0, scaleX: 0 }}
            whileInView={{ opacity: 1, scaleX: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ delay: 0.55, duration: 1, ease: "easeOut" }}
            style={{ transformOrigin: "center" }}
            className="mx-auto mt-16 md:mt-20 w-24 md:w-28 h-0.5 bg-liftile-orange"
          />

          {/* Slogan — large, elegant */}
          <motion.p
            data-testid="promise-slogan"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeUp}
            transition={{ delay: 0.7 }}
            className="mt-7 font-display uppercase text-liftile-cream tracking-[0.18em] text-2xl sm:text-3xl md:text-4xl lg:text-5xl"
          >
            Rise With <span className="text-liftile-orange">Confidence</span>
          </motion.p>
        </div>
      </div>
    </section>
  );
}
